import React, { useState } from 'react';
import './AlphaScanFilters.css';

const AlphaScanFilters = ({ onFiltersChange, activeCategory, currentFilters }) => {
  const [activeTab, setActiveTab] = useState('metrics'); // 'metrics' or 'audit'
  
  // Filter state
  const [filters, setFilters] = useState(currentFilters || {
    // Bonding Curve
    bondingCurveMin: null,
    bondingCurveMax: null,
    
    // Metrics tab
    onlyCTO: false,
    onlyDexPaid: false,
    onlyPumpLive: false,
    marketCapMin: null,
    marketCapMax: null,
    volumeMin: null,
    volumeMax: null,
    netBuyVolumeMin: null,
    netBuyVolumeMax: null,
    netBuyersMin: null,
    netBuyersMax: null,
    holdersMin: null,
    holdersMax: null,
    liquidityMin: 15000,
    liquidityMax: null,
    feesPaidMin: null,
    feesPaidMax: null,
    
    // Audit tab
    mintAuthorityDisabled: true,
    freezeAuthorityDisabled: true,
    atLeastOneSocial: false,
    verified: false,
    tokenAgeMin: null,
    tokenAgeMax: null,
    topHoldersMin: null,
    topHoldersMax: 20,
    devHoldingMin: null,
    devHoldingMax: 20,
    devMigrationsMin: null,
    devMigrationsMax: null,
    organicScoreMin: null,
    organicScoreMax: null,
  });

  // Update filter and notify parent
  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  // Apply filters
  const applyFilters = () => {
    onFiltersChange(filters);
  };

  // Reset filters for current category
  const resetCategory = () => {
    const resetFilters = {
      ...filters,
      bondingCurveMin: null,
      bondingCurveMax: null,
      onlyCTO: false,
      onlyDexPaid: false,
      onlyPumpLive: false,
      marketCapMin: null,
      marketCapMax: null,
      volumeMin: null,
      volumeMax: null,
      netBuyVolumeMin: null,
      netBuyVolumeMax: null,
      netBuyersMin: null,
      netBuyersMax: null,
      holdersMin: null,
      holdersMax: null,
      liquidityMin: 15000,
      liquidityMax: null,
      feesPaidMin: null,
      feesPaidMax: null,
      mintAuthorityDisabled: true,
      freezeAuthorityDisabled: true,
      atLeastOneSocial: false,
      verified: false,
      tokenAgeMin: null,
      tokenAgeMax: null,
      topHoldersMin: null,
      topHoldersMax: 20,
      devHoldingMin: null,
      devHoldingMax: 20,
      devMigrationsMin: null,
      devMigrationsMax: null,
      organicScoreMin: null,
      organicScoreMax: null,
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  return (
    <div className="alphascan-filters-panel">
      <div className="filters-header">
        <h3>AlphaScan Filters</h3>
        <button className="close-filters" onClick={() => {}}>✕</button>
      </div>

      {/* Bonding Curve */}
      <div className="filter-section">
        <label className="filter-label">Bonding Curve %</label>
        <div className="filter-row">
          <input
            type="number"
            placeholder="Min"
            value={filters.bondingCurveMin || ''}
            onChange={(e) => updateFilter('bondingCurveMin', e.target.value ? parseFloat(e.target.value) : null)}
            className="filter-input"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.bondingCurveMax || ''}
            onChange={(e) => updateFilter('bondingCurveMax', e.target.value ? parseFloat(e.target.value) : null)}
            className="filter-input"
          />
        </div>
      </div>

      {/* Tab Selector */}
      <div className="filter-tabs">
        <button 
          className={`filter-tab ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          Metrics ⓘ
        </button>
        <button 
          className={`filter-tab ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit')}
        >
          Audit ⓘ
        </button>
      </div>

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <div className="filter-content">
          {/* Checkboxes */}
          <div className="filter-checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.onlyCTO}
                onChange={(e) => updateFilter('onlyCTO', e.target.checked)}
              />
              Only CTO
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.onlyDexPaid}
                onChange={(e) => updateFilter('onlyDexPaid', e.target.checked)}
              />
              Only Dex Paid
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.onlyPumpLive}
                onChange={(e) => updateFilter('onlyPumpLive', e.target.checked)}
              />
              Only Pump Live
            </label>
          </div>

          {/* Market Cap */}
          <div className="filter-section">
            <label className="filter-label">Market Cap $</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.marketCapMin || ''}
                onChange={(e) => updateFilter('marketCapMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.marketCapMax || ''}
                onChange={(e) => updateFilter('marketCapMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* 24h Volume */}
          <div className="filter-section">
            <label className="filter-label">24h Volume $</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.volumeMin || ''}
                onChange={(e) => updateFilter('volumeMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.volumeMax || ''}
                onChange={(e) => updateFilter('volumeMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* 24h Net Buy Volume */}
          <div className="filter-section">
            <label className="filter-label">24h Net Buy Volume $</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.netBuyVolumeMin || ''}
                onChange={(e) => updateFilter('netBuyVolumeMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.netBuyVolumeMax || ''}
                onChange={(e) => updateFilter('netBuyVolumeMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* 24h Net Buyers */}
          <div className="filter-section">
            <label className="filter-label">24h Net Buyers</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.netBuyersMin || ''}
                onChange={(e) => updateFilter('netBuyersMin', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.netBuyersMax || ''}
                onChange={(e) => updateFilter('netBuyersMax', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Holders */}
          <div className="filter-section">
            <label className="filter-label">Holders</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.holdersMin || ''}
                onChange={(e) => updateFilter('holdersMin', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.holdersMax || ''}
                onChange={(e) => updateFilter('holdersMax', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Liquidity */}
          <div className="filter-section">
            <label className="filter-label">Liquidity $</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.liquidityMin || ''}
                onChange={(e) => updateFilter('liquidityMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.liquidityMax || ''}
                onChange={(e) => updateFilter('liquidityMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Fees Paid (SOL) */}
          <div className="filter-section">
            <label className="filter-label">Fees Paid (SOL)</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.feesPaidMin || ''}
                onChange={(e) => updateFilter('feesPaidMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.feesPaidMax || ''}
                onChange={(e) => updateFilter('feesPaidMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === 'audit' && (
        <div className="filter-content">
          {/* Checkboxes */}
          <div className="filter-checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.mintAuthorityDisabled}
                onChange={(e) => updateFilter('mintAuthorityDisabled', e.target.checked)}
              />
              Mint Auth Disabled
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.freezeAuthorityDisabled}
                onChange={(e) => updateFilter('freezeAuthorityDisabled', e.target.checked)}
              />
              Freeze Auth Disabled
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.atLeastOneSocial}
                onChange={(e) => updateFilter('atLeastOneSocial', e.target.checked)}
              />
              At Least 1 Social
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.verified}
                onChange={(e) => updateFilter('verified', e.target.checked)}
              />
              Verified
            </label>
          </div>

          {/* Token Age */}
          <div className="filter-section">
            <label className="filter-label">Token Age (mins)</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.tokenAgeMin || ''}
                onChange={(e) => updateFilter('tokenAgeMin', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.tokenAgeMax || ''}
                onChange={(e) => updateFilter('tokenAgeMax', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Top 10 Holders % */}
          <div className="filter-section">
            <label className="filter-label">Top 10 Holders %</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.topHoldersMin || ''}
                onChange={(e) => updateFilter('topHoldersMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.topHoldersMax || ''}
                onChange={(e) => updateFilter('topHoldersMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Dev Holding % */}
          <div className="filter-section">
            <label className="filter-label">Dev Holding %</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.devHoldingMin || ''}
                onChange={(e) => updateFilter('devHoldingMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.devHoldingMax || ''}
                onChange={(e) => updateFilter('devHoldingMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Dev Migrations */}
          <div className="filter-section">
            <label className="filter-label">Dev Migrations</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.devMigrationsMin || ''}
                onChange={(e) => updateFilter('devMigrationsMin', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.devMigrationsMax || ''}
                onChange={(e) => updateFilter('devMigrationsMax', e.target.value ? parseInt(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Organic Score */}
          <div className="filter-section">
            <label className="filter-label">Organic Score</label>
            <div className="filter-row">
              <input
                type="number"
                placeholder="Min"
                value={filters.organicScoreMin || ''}
                onChange={(e) => updateFilter('organicScoreMin', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.organicScoreMax || ''}
                onChange={(e) => updateFilter('organicScoreMax', e.target.value ? parseFloat(e.target.value) : null)}
                className="filter-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="filter-actions">
        <button className="reset-btn" onClick={resetCategory}>
          Reset {activeCategory}
        </button>
        <button className="save-btn" onClick={applyFilters}>
          Save All
        </button>
      </div>
    </div>
  );
};

export default AlphaScanFilters;
